import type { AlertColor } from "@mui/material";
import {
  Avatar,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import DashedArrowLeftIcon from "@entrilia/shared/icons/DashedArrowLeftIcon";
import DashedArrowRightIcon from "@entrilia/shared/icons/DashedArrowRightIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import DataBackupIcon from "../../common/domain-icons/DataBackupIcon";
import SnowflakeLogo from "../../../images/snowflake.png";

export interface StartResyncResult {
  severity: AlertColor;
  message: string;
}

interface Props {
  onClose: () => void;
  onStart: (result: StartResyncResult) => void;
}

const StartResyncDialog = ({ onClose, onStart }: Props) => {
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleResyncClick = async () => {
    setLoading(true);
    try {
      const resp = await adminApi.startDwhFullSync();
      if (resp.data) {
        if (resp.data.previousSyncInProgress) {
          onStart({
            severity: "error",
            message: "Re-synchronization is not possible, as the database update process is underway.",
          });
        } else {
          onStart({
            severity: "info",
            message: "Re-synchronization started successfully. Track the progress in the Logs tab.",
          });
        }
      } else {
        logError(resp.error, "[StartResyncDialog] startDwhFullSync");
        onStart({
          severity: "error",
          message: "Re-synchronization failed. Please try checking the Logs tab for details.",
        });
      }
    } catch (error) {
      logError(error, "[StartResyncDialog] startDwhFullSync");
      onStart({
        severity: "error",
        message: "Re-synchronization failed. Please try checking the Logs tab for details.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Re-Synchronization</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent sx={(theme) => ({ px: theme.spacing(4) })}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Stack direction="row" spacing={2.5} sx={{ alignItems: "center", my: 2 }}>
            <DataBackupIcon sx={{ width: 64, height: 64 }} />
            <Stack spacing={1}>
              <DashedArrowRightIcon color="primary" sx={{ width: 61, height: 10 }} />
              <DashedArrowLeftIcon sx={(theme) => ({ width: 61, height: 10, color: theme.palette.divider })} />
            </Stack>
            <Avatar src={SnowflakeLogo} variant="rounded" alt="logo" sx={{ width: 64, height: 64 }} />
          </Stack>
          <Typography variant="subtitle2">Re-Synchronization Snowflake</Typography>
          <Typography color="textSecondary" sx={{ textAlign: "center" }}>
            You are about to begin a full data warehouse resynchronization. While this process is underway, you may
            experience temporary data gaps or inconsistencies in Snowflake, and SmartViews relying on the warehouse may
            be disrupted until the sync completes. The synchronization is expected to take several minutes, and you can
            track its progress in the Logs tab.
          </Typography>
          <FormControlLabel
            control={<Checkbox checked={acknowledged} onChange={(_, checked) => setAcknowledged(checked)} />}
            label="I understand that Snowflake data and SmartViews may be temporarily disrupted."
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          color="primary"
          loading={loading}
          disabled={!acknowledged}
          onClick={handleResyncClick}
        >
          Start
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default StartResyncDialog;
